import { Button, Card, ErrorBox, Field, Input, Note, Page } from '@elsa/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { download } from '../lib/csv.ts';
import { useOrg } from '../lib/session.tsx';
import { app, friendly, must, rpc, supabase } from '../lib/supabase.ts';

// Contul (GDPR, docs/22 §7): profilul, parola, exportul datelor proprii, stergerea contului.
export function AccountPage() {
  const { session } = useOrg();
  const qc = useQueryClient();
  const [pwDone, setPwDone] = useState(false);
  const profile = useQuery({
    queryKey: ['profile', session.user.id],
    queryFn: async () =>
      (
        (await must(
          app().from('profiles').select('full_name, phone').eq('user_id', session.user.id),
        )) as {
          full_name: string | null;
          phone: string | null;
        }[]
      )[0] ?? { full_name: null, phone: null },
  });
  const save = useMutation({
    mutationFn: (p: { full_name: string | null; phone: string | null }) =>
      must(app().from('profiles').update(p).eq('user_id', session.user.id)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['profile'] }),
  });
  const password = useMutation({
    mutationFn: async (p: string) => {
      const { error } = await supabase.auth.updateUser({ password: p });
      if (error) throw new Error(friendly(error.message));
    },
    onSuccess: () => setPwDone(true),
  });
  const exportData = useMutation({
    mutationFn: () => rpc<unknown>('my_data'),
    onSuccess: (d) =>
      download(
        `elsa-datele-mele-${new Date().toISOString().slice(0, 10)}.json`,
        JSON.stringify(d, null, 2),
        'application/json',
      ),
  });
  const del = useMutation({
    mutationFn: () => rpc('delete_my_account'),
    onSuccess: async () => {
      await supabase.auth.signOut();
      location.assign('/');
    },
  });
  const onProfile = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const v = (k: string) => String(f.get(k) ?? '').trim() || null;
    save.mutate({ full_name: v('full_name'), phone: v('phone') });
  };
  return (
    <Page title="Contul meu">
      <Card title="Profil">
        {profile.data ? (
          <form className="grid gap-3 sm:grid-cols-2" onSubmit={onProfile}>
            <Field label="Email">
              <Input value={session.user.email ?? ''} disabled readOnly />
            </Field>
            <Field label="Nume și prenume">
              <Input name="full_name" defaultValue={profile.data.full_name ?? ''} maxLength={120} />
            </Field>
            <Field label="Telefon">
              <Input name="phone" type="tel" defaultValue={profile.data.phone ?? ''} />
            </Field>
            <div className="flex items-end gap-3">
              <Button type="submit" disabled={save.isPending}>
                Salvează
              </Button>
              {save.isSuccess ? <span className="text-xs text-gri-700">Salvat.</span> : null}
            </div>
            <ErrorBox error={save.error ?? profile.error} />
          </form>
        ) : null}
      </Card>
      <Card title="Parola">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            password.mutate(String(new FormData(e.currentTarget).get('password')));
          }}
        >
          <Field label="Parola nouă" help="Cel puțin 8 caractere.">
            <Input
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </Field>
          <Button type="submit" disabled={password.isPending}>
            Schimbă parola
          </Button>
        </form>
        <div className="mt-2">
          <ErrorBox error={password.error} />
          {pwDone ? <Note>Parola s-a schimbat.</Note> : null}
        </div>
      </Card>
      <Card title="Datele mele (GDPR)">
        <div className="space-y-3 text-sm">
          <p>
            Descarci tot ce păstrăm despre contul tău: profilul, firmele, alertele, notificările,
            exporturile.
          </p>
          <Button onClick={() => exportData.mutate()} disabled={exportData.isPending}>
            Descarcă datele mele (JSON)
          </Button>
          <ErrorBox error={exportData.error} />
          <hr className="border-gri-300" />
          <p>
            Ștergerea contului e definitivă. Firmele în care ești singurul membru se șterg odată cu
            contul; dacă firma are colegi, predă-o întâi (Echipă); un abonament plătit se anulează
            întâi din portalul de plată.
          </p>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (confirm('Ștergi definitiv contul?')) del.mutate();
            }}
          >
            <Field label={`Scrie emailul (${session.user.email}) ca să confirmi`}>
              <Input
                name="confirm"
                required
                pattern={(session.user.email ?? '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}
              />
            </Field>
            <Button tone="danger" type="submit" disabled={del.isPending}>
              Șterge contul
            </Button>
          </form>
          <ErrorBox error={del.error} />
        </div>
      </Card>
    </Page>
  );
}
